import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

// Enhanced Service Card with features
const ServiceCard = ({ icon, title, description, features, serviceType, delay }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleDetailClick = () => {
    navigate(`/service/${serviceType}`);
  };

  return (
    <motion.div
      className="bg-gray-800 rounded-lg p-6 shadow-lg hover:shadow-cyan-500/20 transition-all duration-300 border border-gray-700 hover:border-cyan-500"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      viewport={{ once: true, margin: "-100px" }}
      whileHover={{ scale: 1.03 }}
    >
      <div className="w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center mb-6 mx-auto text-cyan-400">
        {icon}
      </div>
      <h3 className="text-xl font-bold mb-3 text-center font-poppins">{title}</h3>
      <p className="text-gray-400 text-center font-roboto mb-4">{description}</p>
      
      {features && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <div className="flex flex-wrap gap-2 justify-center">
            {features.map((feature, index) => (
              <span 
                key={index}
                className="text-xs bg-gray-700 text-cyan-300 px-2 py-1 rounded-full"
              >
                {feature}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Detail Button */}
      <button
        onClick={handleDetailClick}
        className="w-full mt-4 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-blue-700 transform hover:scale-105 transition-all duration-200"
      >
        {t('services.viewDetails')}
      </button>
    </motion.div>
  );
};

const Services = () => {
  const { t } = useTranslation();
  
  // src/components/Services/index.jsx içinde serviceData dizisini güncelle

  const serviceData = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      title: t('services.web.title'),
      description: t('services.web.description'),
      features: t('services.web.features', { returnObjects: true }),
      serviceType: 'web',
      delay: 0.1
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      title: t('services.mobile.title'),
      description: t('services.mobile.description'),
      features: t('services.mobile.features', { returnObjects: true }),
      serviceType: 'mobile',
      delay: 0.2
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      title: t('services.ai.title'),
      description: t('services.ai.description'),
      features: t('services.ai.features', { returnObjects: true }),
      serviceType: 'ai',
      delay: 0.3
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      title: t('services.software.title'),
      description: t('services.software.description'),
      features: t('services.software.features', { returnObjects: true }),
      serviceType: 'software',
      delay: 0.4
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
      title: t('services.automation.title'),
      description: t('services.automation.description'),
      features: t('services.automation.features', { returnObjects: true }),
      serviceType: 'automation',
      delay: 0.5
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: t('services.consulting.title'),
      description: t('services.consulting.description'),
      features: t('services.consulting.features', { returnObjects: true }),
      serviceType: 'consulting',
      delay: 0.6
    }
  ];

  return (
    <div className="container mx-auto px-4 pt-0 mt-0">
      <motion.div
        className="text-center mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true, margin: "-100px" }}
      >
        <h2 className="text-3xl md:text-4xl font-bold mb-4 font-poppins">
          <span className="text-cyan-400">{t('services.titleHighlight')}</span> {t('services.title')}
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto font-roboto">{t('services.subtitle')}</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {serviceData.map((service, index) => (
          <ServiceCard
            key={index}
            icon={service.icon}
            title={service.title}
            description={service.description}
            features={service.features}
            serviceType={service.serviceType}
            delay={service.delay}
          />
        ))}
      </div>
      
      <motion.div
        className="mt-20 text-center"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        viewport={{ once: true, margin: "-100px" }}
      >
        <a
          href="#contact"
          className="px-8 py-3 bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-md transition-colors duration-300 transform hover:scale-105 inline-block"
        >
          {t('services.ctaButton')}
        </a>
      </motion.div>
    </div>
  );
};

export default Services;